import { Typography } from '@mui/material'
import { FC } from 'react'
import { Step } from 'react-joyride'
import { matchPath, useLocation } from 'react-router-dom'
import { useCanEditAndAddDocument } from 'src/pages/pages-behind-login/onboarded-vessel/documents-page/documents-table/documents-table-mui'
import {
  AnchorIcon,
  ComputerIcon,
  DashboardIcon,
  FinanceReportIcon,
  RouteIcon,
  VesselFrontIcon,
} from '~components/icons'
import SVGProps from '~components/icons/config/svg-props'
import { breakpointsWithHamburgerMenu } from '~components/navigation-and-footer/navigation/navigation-context'
import { localStorageItemKeys } from '~constants/local-storage-items'
import { dppPoolNames } from '~constants/pools'
import { ExactScopeName, useHasScope } from '~hooks/permissions/use-has-scope'
import { useGetMe } from '~hooks/queries/me/use-get-me'
import { useGetTabList } from '~hooks/queries/vessels/data/use-get-tab-list'
import { useGetVessel } from '~hooks/queries/vessels/use-get-vessel'
import { useGetVessels } from '~hooks/queries/vessels/use-get-vessels'
import useBreakpoint from '~hooks/use-breakpoint'
import { useConstructLinkWithPool } from '~hooks/use-construct-link-with-pool'
import { usePoolSubPages } from '~hooks/use-pool-sub-pages'
import { useFinancialSubPages } from '~pages/pages-behind-login/financials/use-financial-sub-pages'
import { VESSEL_SWITCH_TRACKS } from '~pages/pages-behind-login/vessel-page/vessel-page-content/sub-components/auto-complete-and-favorites'
import {
  allPartnersOnboardedEnabled,
  getFeatureToggle,
} from '~utils/feature-toggles'
import { useVesselTourLink } from '../hooks/use-pool-points-tour-link'
import { TrackFieldValue, VesselStatus } from '~types/vessel.types'
import { constructCargoListTour } from './cargo-list-tour'
import {
  constructCertificatesTour,
  constructTechManagerCertificatesTour,
} from './certificates-tour'
import { lastStep, lastStepNoLink, loadingStep } from './common-steps'
import { constructDashboardTour } from './dashboard-tour'
import { constructDppPartnerTour } from './dpp-partner-tour'
import { constructFinancialsTour } from './financials-tour'
import { constructFleetTour } from './fleet-tour'
import { constructPortInfoTour } from './port-info-tour'

const CERTIFICATES_PATH = 'fleet/vessels/:vesselId/documents'
const DASHBOARD_PATH = 'dashboard/:pool'
const HELP_PATH = 'help'
const GUIDED_TOURS_LAUNCH_DATE = '2026-11-04'

export interface GuidedTourListItem {
  id: string
  name: string
  status: GuidedTourStatus
  tourStartLink: string
  content: Step[]
  tourPaths: string[]
  startTourPath: string
  autoStartForUsersJoinedFrom?: string
  isApplicable?: boolean
  isLoading?: boolean
  icon: FC<SVGProps>
}

export type GuidedTourStatus = 'Not Seen' | 'Started' | 'Skipped' | 'Completed'

export function useGuidedToursList(): GuidedTourListItem[] {
  const { data, isLoading, isError } = useGetMe()
  const { breakpoint } = useBreakpoint()
  const location = useLocation()
  const isMtUser = useHasScope(ExactScopeName.maerskTankersUser)
  const isDppPartner = useHasScope(
    ...dppPoolNames.map((pool) => `pool:${pool}:partner`),
  )
  const isCargoListUpdater = useHasScope(ExactScopeName.charteringUpdate)
  const dashboardLink = useConstructLinkWithPool(`/${DASHBOARD_PATH}`)
  const poolSubPages = usePoolSubPages()
  const financialSubPages = useFinancialSubPages()
  const canReadPortInfo = useHasScope(
    ExactScopeName.technicalManagement,
    ExactScopeName.portInfoRead,
    ExactScopeName.portInfoWrite,
  )
  const canWritePortInfo = useHasScope(
    ExactScopeName.technicalManagement,
    ExactScopeName.portInfoWrite,
  )
  const vesselId = matchPath({ path: CERTIFICATES_PATH }, location.pathname)
    ?.params.vesselId
  const canEditDocuments = useCanEditAndAddDocument()
  const { data: vessel } = useGetVessel({ vesselId })
  const isOnCertificatesPage = Boolean(vesselId)
  const isOnHelpPage = Boolean(
    matchPath({ path: HELP_PATH }, location.pathname),
  )
  const onboardingEligible = isMtUser || isDppPartner
  const earningsPool =
    financialSubPages
      .find(({ title }) => title === 'Earnings')
      ?.link.split('/')
      .at(-1) ?? ''
  const needsFinancialsVessel =
    Boolean(earningsPool) &&
    (isOnHelpPage ||
      location.pathname.startsWith('/financials/') ||
      localStorage.getItem(localStorageItemKeys.triggeredGuidedTour) ===
        'financials')
  const availableDppPools = dppPoolNames.filter(
    (pool) => isMtUser || data?.scope.includes(`pool:${pool}:partner`),
  )
  const onboardingPool =
    availableDppPools.find(
      (name) => dashboardLink === `/dashboard/${name.toLowerCase()}`,
    ) ?? availableDppPools[0]
  const { data: vessels, isError: vesselsError } = useGetVessels({
    queryDisabled: !isOnHelpPage && !needsFinancialsVessel,
  })
  const financialsVessel = useVesselTourLink({
    vessels,
    pool: earningsPool,
    enabled: needsFinancialsVessel,
    type: 'financials',
    requiredPaths: ['adjustments', 'pool-points'],
  })
  const fleetTourVessel = vessels?.[0]
  const {
    data: fleetOperationsTabs,
    isSuccess: fleetOperationsTabsLoaded,
    isError: fleetOperationsTabsError,
  } = useGetTabList({
    vesselId: fleetTourVessel?.vesselId,
    type: 'operations',
  })
  const techManagedVesselId = vessels?.find(
    (item) =>
      item.technicallyManaged &&
      item.track !== TrackFieldValue.TC_OUT &&
      item.status !== VesselStatus.TC_OUT,
  )?.vesselId
  const standardVesselId = vessels?.find(
    (item) => !item.technicallyManaged,
  )?.vesselId
  const isCommercialView = data?.settings?.certificates?.commercialView ?? false
  const isStandardCertificatesTourApplicable =
    vessel?.technicallyManaged === false
  const isTechManagerCertificatesTourApplicable = Boolean(
    vessel?.technicallyManaged &&
    vessel.track !== TrackFieldValue.TC_OUT &&
    vessel.status !== VesselStatus.TC_OUT &&
    !isCommercialView,
  )
  const hasVesselSwitch = Boolean(
    isStandardCertificatesTourApplicable &&
    vessel &&
    VESSEL_SWITCH_TRACKS.includes(vessel.track),
  )
  const hasStandardCertificateStatusChips = Boolean(
    vessel?.expiredCertificates?.length || vessel?.missingCertificates?.length,
  )
  const hasTechManagerCertificateStatusChips = Boolean(
    vessel?.certificatesWithUpcomingSurvey?.length ||
    vessel?.expiringCertificatesWithin90Days?.length ||
    vessel?.expiredCertificates?.length ||
    vessel?.missingCertificates?.length,
  )

  if (isLoading || isError) return []

  const guidedTours = data?.settings?.guidedTours || []

  function getTourStatus(tourId: string): GuidedTourStatus {
    return guidedTours?.find((tour) => tour.id === tourId)?.status ?? 'Not Seen'
  }

  const tours: GuidedTourListItem[] = []

  if (onboardingEligible) {
    const pool = onboardingPool
    const tourStartLink = `/dashboard/${pool.toLowerCase()}`

    tours.push({
      id: 'dpp-partner-onboarding',
      name: 'Getting started with InSite',
      status: getTourStatus('dpp-partner-onboarding'),
      tourStartLink,
      content: constructDppPartnerTour({
        breakpoint,
        hasBasecampLink: getFeatureToggle(allPartnersOnboardedEnabled),
        hasKeyFigures: pool !== 'Suezmax',
        pool: pool.toLowerCase(),
      }),
      tourPaths: [
        tourStartLink,
        `/financials/earnings/${pool.toLowerCase()}`,
        `/financials/forecast/${pool.toLowerCase()}`,
        '/fleet/vessels',
        `/documents/${pool.toLowerCase()}`,
        '/help',
        '/profile',
      ],
      startTourPath: tourStartLink,
      autoStartForUsersJoinedFrom: GUIDED_TOURS_LAUNCH_DATE,
      icon: ComputerIcon,
    })
  }

  if (dashboardLink !== '/404') {
    tours.push({
      id: '6',
      name: 'Dashboard',
      status: getTourStatus('6'),
      tourStartLink: dashboardLink,
      content: constructDashboardTour({
        breakpoint,
        hasMultiplePools: poolSubPages.length > 1,
      }),
      tourPaths: [DASHBOARD_PATH],
      startTourPath: DASHBOARD_PATH,
      autoStartForUsersJoinedFrom: GUIDED_TOURS_LAUNCH_DATE,
      icon: DashboardIcon,
    })
  }

  const financialsVesselReady =
    !needsFinancialsVessel ||
    vesselsError ||
    financialsVessel.isSuccess ||
    financialsVessel.isError

  if (financialSubPages.length > 0) {
    tours.push({
      id: 'financials',
      name: 'Financials',
      status: getTourStatus('financials'),
      tourStartLink: financialSubPages[0].link,
      isLoading: !financialsVesselReady,
      content: financialsVesselReady
        ? constructFinancialsTour({
            breakpoint,
            subpages: financialSubPages,
            vesselLink: financialsVessel.data ?? undefined,
          })
        : [loadingStep],
      tourPaths: ['financials/*'],
      startTourPath: 'financials/*',
      autoStartForUsersJoinedFrom: GUIDED_TOURS_LAUNCH_DATE,
      icon: FinanceReportIcon,
    })
  }

  const fleetTourTabsReady =
    !fleetTourVessel || fleetOperationsTabsLoaded || fleetOperationsTabsError
  const fleetTourReady = vesselsError || Boolean(vessels && fleetTourTabsReady)

  if (fleetTourReady) {
    tours.push({
      id: 'fleet',
      name: 'Fleet',
      status: getTourStatus('fleet'),
      tourStartLink: '/fleet/vessels',
      content: constructFleetTour({
        breakpoint,
        vessel: fleetTourVessel,
        operationsTabs: fleetOperationsTabs ?? [],
      }),
      tourPaths: ['fleet/vessels', 'fleet/vessels/:vesselId/*'],
      startTourPath: 'fleet/vessels',
      autoStartForUsersJoinedFrom: GUIDED_TOURS_LAUNCH_DATE,
      icon: VesselFrontIcon,
    })
  }

  if (isMtUser || isCargoListUpdater) {
    tours.push({
      id: '2',
      name: 'Cargo List',
      status: getTourStatus('2'),
      tourStartLink: '/chartering/cargo-list',
      content: constructCargoListTour({
        breakpoint,
        hasUpdateScope: isCargoListUpdater,
      }),
      tourPaths: ['chartering/cargo-list'],
      startTourPath: 'chartering/cargo-list',
      autoStartForUsersJoinedFrom: GUIDED_TOURS_LAUNCH_DATE,
      icon: RouteIcon,
    })
  }

  if (canReadPortInfo) {
    tours.push({
      id: '3',
      name: 'Port Info',
      status: getTourStatus('3'),
      tourStartLink:
        '/port-info?activeCategory=countries&category=ports&category=countries',
      content: constructPortInfoTour({
        breakpoint,
        hasWriteScope: canWritePortInfo,
      }),
      tourPaths: ['port-info'],
      startTourPath: 'port-info',
      autoStartForUsersJoinedFrom: GUIDED_TOURS_LAUNCH_DATE,
      icon: AnchorIcon,
    })
  }

  if (isOnCertificatesPage || standardVesselId) {
    tours.push({
      id: '4',
      name: 'Certificates',
      status: getTourStatus('4'),
      tourStartLink: standardVesselId
        ? `/fleet/vessels/${standardVesselId}/documents`
        : '/fleet',
      content: constructCertificatesTour({
        breakpoint,
        hasCertificateStatusChips: hasStandardCertificateStatusChips,
        hasWriteScope: canEditDocuments,
        hasVesselSwitch,
        isMtUser,
      }),
      tourPaths: [CERTIFICATES_PATH],
      startTourPath: CERTIFICATES_PATH,
      autoStartForUsersJoinedFrom: GUIDED_TOURS_LAUNCH_DATE,
      isApplicable: isStandardCertificatesTourApplicable,
      icon: VesselFrontIcon,
    })
  }

  if (isMtUser && (isOnCertificatesPage || techManagedVesselId)) {
    tours.push({
      id: '5',
      name: 'Certificates (Technically Managed Vessels)',
      status: getTourStatus('5'),
      tourStartLink: techManagedVesselId
        ? `/fleet/vessels/${techManagedVesselId}/documents`
        : '/fleet',
      content: constructTechManagerCertificatesTour({
        breakpoint,
        hasCertificateStatusChips: hasTechManagerCertificateStatusChips,
        hasWriteScope: canEditDocuments,
      }),
      tourPaths: [CERTIFICATES_PATH],
      startTourPath: CERTIFICATES_PATH,
      autoStartForUsersJoinedFrom: GUIDED_TOURS_LAUNCH_DATE,
      isApplicable: isTechManagerCertificatesTourApplicable,
      icon: VesselFrontIcon,
    })
  }

  const hasHamburgerMenu = breakpointsWithHamburgerMenu.includes(breakpoint)

  return tours
    .map((tour) => withFinalStepOnStartPage(tour, hasHamburgerMenu))
    .map((tour, _, finalTours) => withNextTourSuggestion(tour, finalTours))
}

function withFinalStepOnStartPage(
  tour: GuidedTourListItem,
  hasHamburgerMenu: boolean,
): GuidedTourListItem {
  const finalStep = tour.content.at(-1)
  const lastTourStep = tour.content.at(-2)
  if (!finalStep || tour.isLoading) return tour

  const pages = tour.content.reduce<string[]>(
    (stepPages, _, index) => [
      ...stepPages,
      index === 0
        ? tour.tourStartLink
        : (tour.content[index - 1].data?.next ?? stepPages[index - 1]),
    ],
    [],
  )
  const lastTourStepPage = pages.at(-2)
  const returnsToStart =
    Boolean(lastTourStep) &&
    lastTourStepPage !== tour.tourStartLink &&
    Boolean(
      matchPath({ path: tour.startTourPath }, tour.tourStartLink.split('?')[0]),
    )

  return {
    ...tour,
    content: [
      ...tour.content.slice(0, -2),
      ...(lastTourStep
        ? [
            returnsToStart
              ? {
                  ...lastTourStep,
                  data: { ...lastTourStep.data, next: tour.tourStartLink },
                }
              : lastTourStep,
          ]
        : []),
      {
        ...finalStep,
        ...(hasHamburgerMenu
          ? { target: 'body', placement: 'center' as const }
          : {
              target: () =>
                document.querySelector<HTMLElement>('.tour-step-nav-help') ??
                document.body,
              placement: 'right' as const,
            }),
        skipBeacon: true,
        data: {
          ...finalStep.data,
          ...(returnsToStart && { previous: lastTourStepPage }),
          deferredTarget: true,
        },
      },
    ],
  }
}

function withNextTourSuggestion(
  tour: GuidedTourListItem,
  tours: GuidedTourListItem[],
): GuidedTourListItem {
  const finalStep = tour.content.at(-1)
  const isCongratulationsStep =
    finalStep?.content === lastStep.content ||
    finalStep?.content === lastStepNoLink.content

  if (!finalStep || !isCongratulationsStep || finalStep.data?.startTour) {
    return tour
  }

  const nextTour = tours.find(
    (candidate) =>
      candidate.id !== tour.id &&
      candidate.status === 'Not Seen' &&
      candidate.isApplicable !== false &&
      matchPath(
        { path: candidate.startTourPath },
        candidate.tourStartLink.split('?')[0],
      ),
  )

  if (!nextTour) return tour

  return {
    ...tour,
    content: [
      ...tour.content.slice(0, -1),
      {
        ...finalStep,
        content: (
          <>
            {finalStep.content}
            <Typography variant="paragraph2" component="p" sx={{ mt: 2 }}>
              Would you like a deeper dive into the {nextTour.name}?
            </Typography>
          </>
        ),
        data: {
          ...finalStep.data,
          startTour: {
            id: nextTour.id,
            link: nextTour.tourStartLink,
            label: `Start ${nextTour.name} tour`,
          },
        },
      },
    ],
  }
}
